import React, { useState, useMemo, useEffect, useContext } from 'react';
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import Chip from "@mui/material/Chip";
import Badge from "@mui/material/Badge";
import Paper from "@mui/material/Paper";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import EventCard from './EventCard';
import { PAContext } from "../../../Services/PAContext";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import PAEvent from "../../../MasterEventObjects/PAEvent";
import PAAttendee from "../../../MasterEventObjects/Attendee";
import {deleteEvent} from "./BrowserDatabaseFunctions";

const chipSelectedStyle = {
    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    color: '#fff',
    fontWeight: 700,
    border: '1px solid #3b82f6',
    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)'
};

const chipUnselectedStyle = {
    background: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    '&:hover': {
        background: '#e2e8f0',
        color: '#0f172a'
    }
};

function Events(props) {

    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();

    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { AdminsActions, Account } = dataArray;

    const [allEvents, setAllEvents] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [eventTargetToDelete, setEventTargetToDelete] = useState(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [sortDirection, setSortDirection] = useState('desc');
    const [selectedGroup, setSelectedGroup] = useState("All");

    useEffect(() => {
        try {
            sendSocketMessage({ action: "adminServicesGetMasterEventsList" });
        } catch (e) {
            console.error("Socket fetch failed for events", e);
        }
    }, []);

    useEffect(() => {
        for (let i = AdminsActions.length - 1; i >= 0; i--) {
            const _incoming = AdminsActions[i];

            if (_incoming.action === "adminServicesGetMasterEventsListResults" || _incoming.action === "eventsList") {
                setAllEvents(_incoming.events_list || []);
                AdminsActions.splice(i, 1);
            }
            else if (_incoming.action === "adminServicesDeleteEventResults") {
                if (_incoming.result === "ok") {
                    const targetedId = String(_incoming.deleted_event_id);

                    setAllEvents(prev => prev.filter(evt => {
                        const baseId = evt.id || (evt._id && typeof evt._id === 'string' ? evt._id : '');
                        return String(baseId) !== targetedId;
                    }));

                    //now delete from browser database
                    // 🟢 Pass the ID and catch any async background database errors safely
                    deleteEvent(targetedId).catch(err => {
                        console.error("Failed to sync IndexedDB deletion:", err);
                    });

                    //now remove from PA_GlobalObject.Planning if the event is currenly loaded
                    if (PA_GlobalObject?.Planning && String(targetedId) === String(PA_GlobalObject.Planning.id || PA_GlobalObject.Planning._id)) {
                        const _new_event = new PAEvent();
                        _new_event.title = "My New Event";
                        _new_event.status = 10;

                        // Fallback to empty strings if Account isn't loaded completely yet
                        const _planner = new PAAttendee(
                            Account?.fname || "",
                            Account?.lname || "",
                            Account?.email || ""
                        );

                        if (Account) {
                            _planner.id = Account.id;
                            _planner.created = Account.created;
                            _planner.role = Account.role;
                            _planner.nickname = Account.nickname;
                            _planner.language = Account.language;
                            _planner.avatar = Account.avatar;
                            _planner.preferred_airport = Account.preferred_airport;
                            _planner.address = Account.address;
                            _planner.city = Account.city;
                            _planner.state = Account.state;
                            _planner.zip = Account.zip;
                            _planner.country = Account.country;
                            _planner.dob = Account.dob;
                            _planner.phone_prefix = Account.phone_prefix;
                            _planner.phone = Account.phone;
                            _planner.mask_phone_number = Account.mask_phone_number;
                            _planner.events = Account.events;
                            _planner.documents = Account.documents;

                            // Add them to the host tracking array
                            _new_event.host = [Account.id];
                        }

                        _new_event.attendees.push(_planner);
                        console.log("_new_event compiled successfully", _new_event);

                        // Re-assign into global app state wrapper
                        if (typeof set_PA_GlobalObject === 'function') {
                            set_PA_GlobalObject({ Planning: _new_event });
                        }
                    }

                } else {
                    alert(`Deletion failed: ${_incoming.message || 'Unknown server fault'}`);
                }
                AdminsActions.splice(i, 1);
            }
        }
    }, [AdminsActions]);

    useEffect(() => {
        const trimmed = searchQuery.trim();
        if (trimmed.length === 1) {
            setSelectedGroup(trimmed.toUpperCase());
        } else if (trimmed.length === 0) {
            setSelectedGroup("All");
        } else {
            setSelectedGroup("None");
        }
    }, [searchQuery]);

    const handleOpenDeleteConfirmation = (eventObj) => {
        setEventTargetToDelete(eventObj);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setEventTargetToDelete(null);
        setDeleteDialogOpen(false);
    };

    const handleConfirmDeleteExecution = () => {
        if (!eventTargetToDelete) return;

        console.log("eventTargetToDelete : ", eventTargetToDelete);

        const targetIdString = eventTargetToDelete._id && typeof eventTargetToDelete._id === 'object'
            ? eventTargetToDelete._id.$oid
            : eventTargetToDelete._id;

        console.log("targetIdString : ", targetIdString);

        sendSocketMessage({
            action: "adminServicesDeleteEvent",
            event_id: targetIdString
        });
        handleCloseDeleteDialog();
    };

    const alphabetList = useMemo(() => "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split(""), []);

    const letterCounts = useMemo(() => {
        const counts = {};
        allEvents.forEach(event => {
            const name = (event.name || event.title || "").trim();
            if (name.length > 0) {
                const firstLetter = name.charAt(0).toUpperCase();
                counts[firstLetter] = (counts[firstLetter] || 0) + 1;
            }
        });
        return counts;
    }, [allEvents]);

    const processedEvents = useMemo(() => {
        let result = [...allEvents];

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            result = result.filter(event => {
                const titleMatch = (event.title || '').toLowerCase();
                const nameMatch = (event.name || '').toLowerCase();

                if (query.length === 1) {
                    return titleMatch.startsWith(query) || nameMatch.startsWith(query);
                }
                return titleMatch.includes(query) || nameMatch.includes(query);
            });
        }

        if (startDate || endDate) {
            result = result.filter(event => {
                const timeTableDates = event.time_table?.dates || [];

                if (timeTableDates.length > 0) {
                    return timeTableDates.some(d => {
                        if (!d.date) return false;
                        if (startDate && d.date < startDate) return false;
                        if (endDate && d.date > endDate) return false;
                        return true;
                    });
                } else if (event.calendar?.begin_date) {
                    const calStart = event.calendar.begin_date.substring(0, 10);
                    const calEnd = event.calendar.end_date ? event.calendar.end_date.substring(0, 10) : calStart;

                    if (startDate && calEnd < startDate) return false;
                    if (endDate && calStart > endDate) return false;
                    return true;
                }
                return false;
            });
        }

        result.sort((a, b) => {
            let aTime = 0;
            let bTime = 0;

            const aDates = a.time_table?.dates || [];
            if (aDates.length > 0 && aDates[0].date) {
                aTime = new Date(aDates[0].date).getTime();
            } else if (a.calendar?.begin_date) {
                aTime = new Date(a.calendar.begin_date).getTime();
            }

            const bDates = b.time_table?.dates || [];
            if (bDates.length > 0 && bDates[0].date) {
                bTime = new Date(bDates[0].date).getTime();
            } else if (b.calendar?.begin_date) {
                bTime = new Date(b.calendar.begin_date).getTime();
            }

            return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
        });

        return result;
    }, [allEvents, searchQuery, startDate, endDate, sortDirection]);

    return (
        <Box sx={{ width: '100%', height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Paper sx={{ p: 3, background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Badge badgeContent={processedEvents.length} color="primary" max={999} sx={{ '& .MuiBadge-badge': { right: -15, top: 10, fontWeight: 700 } }}>
                        <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                            Master Events Registry
                        </Typography>
                    </Badge>
                </Box>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            placeholder="Search by title or keyword..."
                            size="small"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { color: '#0f172a', background: '#f8fafc', '& fieldset': { borderColor: '#cbd5e1' }, '&:hover fieldset': { borderColor: '#94a3b8' } } }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#64748b' }} /></InputAdornment> }}
                        />
                    </Grid>

                    <Grid item xs={6} sm={3} md={2}>
                        <TextField
                            fullWidth
                            label="Start Date Window"
                            type="date"
                            size="small"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root': { color: '#0f172a', background: '#f8fafc', '& fieldset': { borderColor: '#cbd5e1' } }, '& .MuiInputLabel-root': { color: '#475569' } }}
                        />
                    </Grid>

                    <Grid item xs={6} sm={3} md={2}>
                        <TextField
                            fullWidth
                            label="End Date Window"
                            type="date"
                            size="small"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root': { color: '#0f172a', background: '#f8fafc', '& fieldset': { borderColor: '#cbd5e1' } }, '& .MuiInputLabel-root': { color: '#475569' } }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { color: '#0f172a', background: '#f8fafc', '& fieldset': { borderColor: '#cbd5e1' } }, '& .MuiInputLabel-root': { color: '#475569' } }}>
                            <InputLabel id="sort-order-label">Chronological Sorting Direction</InputLabel>
                            <Select
                                labelId="sort-order-label"
                                value={sortDirection}
                                label="Chronological Sorting Direction"
                                onChange={(e) => setSortDirection(e.target.value)}
                                startAdornment={<InputAdornment position="start"><SortIcon sx={{ color: '#64748b', mr: 0.5 }} /></InputAdornment>}
                            >
                                <MenuItem value="desc">Newest Events First</MenuItem>
                                <MenuItem value="asc">Oldest Events First</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1.5, mb: 3, borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', alignItems: 'center', justifyContent: 'center' }}>
                    <Chip
                        label="All Events"
                        clickable
                        size="small"
                        onClick={() => setSearchQuery("")}
                        sx={selectedGroup === "All" ? chipSelectedStyle : chipUnselectedStyle}
                    />
                    {alphabetList.map(letter => {
                        const letterCount = letterCounts[letter] || 0;
                        return (
                            <Badge
                                key={`alpha-badge-${letter}`}
                                badgeContent={letterCount}
                                color="primary"
                                invisible={letterCount === 0}
                                max={99}
                                sx={{ '& .MuiBadge-badge': { fontSize: '0.62rem', height: '16px', minWidth: '16px', top: 2, right: 2, background: '#7c3aed', color: '#fff' } }}
                            >
                                <Chip
                                    label={letter}
                                    disabled={letterCount === 0}
                                    clickable={letterCount > 0}
                                    size="small"
                                    onClick={() => letterCount > 0 && setSearchQuery(letter)}
                                    sx={{
                                        width: '32px', height: '28px', fontSize: '0.78rem',
                                        ...(selectedGroup === letter ? chipSelectedStyle : chipUnselectedStyle)
                                    }}
                                />
                            </Badge>
                        );
                    })}
                </Box>

                <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1, pb: 2, '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '4px' } }}>
                    {processedEvents.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 12, border: '1px dashed #cbd5e1', borderRadius: '12px', background: '#f8fafc' }}>
                            <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 500 }}>
                                No active events match those combined filter parameters.
                            </Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {processedEvents.map((event, index) => {
                                const uniqueKey = event._id && typeof event._id === 'object' ? (event._id.$oid || `evt-${index}`) : (event._id || `evt-${index}`);
                                return (
                                    <Grid item key={uniqueKey} xs={12} sm={6} md={4}>
                                        <EventCard
                                            event={event}
                                            currentUserId={Account?.id}
                                            onDeleteClick={handleOpenDeleteConfirmation}
                                        />
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Box>
            </Paper>

            <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} PaperProps={{ sx: { background: '#ffffff', border: '1px solid #e2e8f0', color: '#0f172a', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Confirm Deletion</DialogTitle>
                <DialogContent><DialogContentText sx={{ color: '#475569' }}>Are you sure you want to permanently delete <strong>{eventTargetToDelete?.name || eventTargetToDelete?.title}</strong>? This action cannot be undone.</DialogContentText></DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={handleCloseDeleteDialog} sx={{ color: '#64748b' }}>Cancel</Button><Button onClick={handleConfirmDeleteExecution} variant="contained" color="error" sx={{ borderRadius: '8px', fontWeight: 600 }}>Delete Event</Button></DialogActions>
            </Dialog>
        </Box>
    );
}

export default Events;